import moment from './Moment'

import { ITheme } from '../api/Data'
import { PROJECT_TYPE_OPTIONS, ProjectType } from '../config/projectTypes'

export type WatermarkPosition = 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight'
export type WatermarkDuration = 'first10Seconds' | 'entireVideo'
export type AttributionPrefix =
  | 'Name only'
  | 'Credit'
  | 'Contributed by'
  | 'Video by'
  | 'Source'
  | 'Submitted by'
  | 'Courtesy of'
export type AttributionStyle = 'lowerThird' | 'plain'

export interface ProjectAttributionSettings {
  showWatermark: boolean
  watermarkPosition: WatermarkPosition
  watermarkDuration: WatermarkDuration
  showContributorAttribution: boolean
  attributionPrefix: AttributionPrefix
  attributionStyle: AttributionStyle
  showTimestamp: boolean
}

export const DEFAULT_PROJECT_ATTRIBUTION_SETTINGS: ProjectAttributionSettings = {
  showWatermark: true,
  watermarkPosition: 'topRight',
  watermarkDuration: 'first10Seconds',
  showContributorAttribution: true,
  attributionPrefix: 'Contributed by',
  attributionStyle: 'lowerThird',
  showTimestamp: false,
}

export const WATERMARK_POSITION_OPTIONS = [
  { key: 'topRight', text: 'Top right', value: 'topRight' },
  { key: 'topLeft', text: 'Top left', value: 'topLeft' },
  { key: 'bottomRight', text: 'Bottom right', value: 'bottomRight' },
  { key: 'bottomLeft', text: 'Bottom left', value: 'bottomLeft' },
]

export const WATERMARK_DURATION_OPTIONS = [
  { key: 'first10Seconds', text: 'First 10 seconds (recommended)', value: 'first10Seconds' },
  { key: 'entireVideo', text: 'Entire video', value: 'entireVideo' },
]

export const ATTRIBUTION_PREFIX_OPTIONS: AttributionPrefix[] = [
  'Name only',
  'Credit',
  'Contributed by',
  'Video by',
  'Source',
  'Submitted by',
  'Courtesy of',
]

export const formatAttribution = (prefix: AttributionPrefix, contributorName: string) =>
  prefix === 'Name only' ? contributorName : `${prefix}: ${contributorName}`

export const suggestProjectName = (teamName: string, projectType: ProjectType, date = new Date()) => {
  const workflowName =
    PROJECT_TYPE_OPTIONS.find((option) => option.value === projectType)?.text.toLowerCase() || 'video'
  const suffix = ` video ${workflowName} project ${moment(date).format('D MMM YYYY')}`
  const availableTeamNameLength = Math.max(0, 75 - suffix.length)

  return `${teamName.slice(0, availableTeamNameLength)}${suffix}`
}

// The API still requires a published master theme when a project is created. Prefer the
// historical neutral theme when it exists, but any published theme is a safe seed because
// its project-specific child is replaced with the canonical attribution layout immediately.
export const findProjectThemeSeed = (themes: ITheme[]) =>
  themes.find((theme) => theme.title.trim().toLowerCase() === 'start from scratch') ||
  themes.find((theme) => Boolean(theme.id)) ||
  null

interface AttributionBranding {
  primaryColor?: string
  buttonTextColor?: string
  primaryFont?: string
  watermarkId?: number
}

const FONT_FALLBACK = 'google/Roboto-Regular.ttf'
const WIDTH = 1920
const HEIGHT = 1080
const WATERMARK_WIDTH = 300
const WATERMARK_HEIGHT = 169
const WATERMARK_PADDING = 72

const getWatermarkCoordinates = (position: WatermarkPosition) => ({
  x: position.endsWith('Right') ? WIDTH - WATERMARK_WIDTH - WATERMARK_PADDING : WATERMARK_PADDING,
  y: position.startsWith('bottom') ? HEIGHT - WATERMARK_HEIGHT - WATERMARK_PADDING : WATERMARK_PADDING,
})

const findExistingElement = (theme: ITheme, predicate: (element: any) => boolean) => {
  const sections = [theme.data?.opening, theme.data?.vloggi, theme.data?.closing]

  for (const section of sections) {
    const found = section?.elements?.find(predicate)
    if (found) return found
  }

  return null
}

export const createAttributionTheme = (
  theme: ITheme,
  settings: ProjectAttributionSettings,
  branding: AttributionBranding = {}
): ITheme => {
  const nextTheme = JSON.parse(JSON.stringify(theme)) as ITheme
  const existingLogo = findExistingElement(theme, (element) => element.name === 'logo')
  const existingText = findExistingElement(theme, (element) => element.type === 'text' && element.fontfile)
  const primaryColor = branding.primaryColor || '#6C58FB'
  const foregroundColor = branding.buttonTextColor || '#FFFFFF'
  const fontfile =
    existingText?.fontfile ||
    (branding.primaryFont ? `google/${branding.primaryFont.replace(/ /g, '_')}-Regular.ttf` : FONT_FALLBACK)
  const logoSource = branding.watermarkId || existingLogo?.src || 'default-logo-watermark'
  const watermarkCoordinates = getWatermarkCoordinates(settings.watermarkPosition)
  const watermark = {
    ...watermarkCoordinates,
    name: 'logo',
    type: 'image',
    src: logoSource,
    width: WATERMARK_WIDTH,
    height: WATERMARK_HEIGHT,
    opacity: 0.8,
    fadeIn: '0.5,0.5',
    ...(settings.watermarkDuration === 'first10Seconds' ? { fadeOut: '10,0.5' } : {}),
  }
  const centeredText = {
    name: 'dynamic',
    type: 'text',
    align: 'center',
    valign: 'middle',
    padding: '0,0',
    fontfile,
    fontcolor: `${foregroundColor}@1`,
    uppercase: false,
  }
  const brandColorBackground = {
    x: 0,
    y: 0,
    name: 'static',
    text: ' ',
    type: 'text',
    align: 'left',
    width: WIDTH,
    height: HEIGHT,
    valign: 'top',
    padding: '0,0',
    fontfile,
    fontsize: 1,
    fontcolor: `${primaryColor}@1`,
    uppercase: false,
    background: `${primaryColor}@1`,
  }
  const clipElements: any[] = settings.showWatermark ? [{ ...watermark }] : []

  if (settings.showContributorAttribution) {
    clipElements.push({
      x: 96,
      y: settings.attributionStyle === 'lowerThird' ? 860 : 900,
      name: 'dynamic',
      text: formatAttribution(settings.attributionPrefix, '{{contributorName}}'),
      type: 'text',
      align: 'left',
      width: 1120,
      height: settings.attributionStyle === 'lowerThird' ? 100 : 64,
      valign: 'middle',
      padding: settings.attributionStyle === 'lowerThird' ? '18,28' : '0,0',
      fontfile,
      fontsize: settings.attributionStyle === 'lowerThird' ? 38 : 34,
      fontcolor: '#FFFFFF@1',
      uppercase: false,
      background: settings.attributionStyle === 'lowerThird' ? `${primaryColor}@0.88` : null,
      fadeIn: '0.5,0.5',
      fadeOut: '5,0.5',
    })
  }

  if (settings.showTimestamp) {
    clipElements.push({
      x: 96,
      y: settings.showContributorAttribution ? 972 : 900,
      name: 'dynamic',
      text: '{{date}}',
      type: 'text',
      align: 'left',
      width: 700,
      height: 48,
      valign: 'middle',
      padding: '0,0',
      fontfile,
      fontsize: 26,
      fontcolor: '#FFFFFF@1',
      uppercase: false,
      background: null,
      fadeIn: '0.5,0.5',
      fadeOut: '5,0.5',
    })
  }

  nextTheme.data = {
    opening: {
      input: 'blackBackground:',
      duration: 5,
      elements: [
        brandColorBackground,
        {
          ...centeredText,
          x: 210,
          y: 390,
          text: '{{storyName}} by {{producerName}}',
          width: 1500,
          height: 180,
          fontsize: 64,
          fadeIn: '0.5,0.5',
          fadeOut: '4,0.5',
        },
        ...(settings.showWatermark ? [{ ...watermark, fadeOut: '4,0.5' }] : []),
      ],
    },
    vloggi: {
      elements: clipElements,
    },
    closing: {
      input: 'blackBackground:',
      duration: 8,
      elements: [
        brandColorBackground,
        ...(settings.showContributorAttribution
          ? [
              {
                ...centeredText,
                name: 'static',
                x: 310,
                y: 190,
                text: 'Video contributed by',
                width: 1300,
                height: 60,
                fontsize: 32,
                fadeIn: '0.5,0.5',
              },
              {
                ...centeredText,
                x: 310,
                y: 270,
                text: '{{contributorList}}',
                width: 1300,
                height: 360,
                fontsize: 44,
                fadeIn: '0.5,0.5',
              },
            ]
          : []),
        {
          ...centeredText,
          name: 'static',
          x: 310,
          y: settings.showContributorAttribution ? 680 : 470,
          text: 'Produced by',
          width: 1300,
          height: 50,
          fontsize: 28,
          fadeIn: '0.5,0.5',
        },
        {
          ...centeredText,
          x: 310,
          y: settings.showContributorAttribution ? 740 : 530,
          text: '{{producerName}}',
          width: 1300,
          height: 70,
          fontsize: 38,
          fadeIn: '0.5,0.5',
          fadeOut: '7,0.5',
        },
        ...(settings.showWatermark ? [{ ...watermark, fadeOut: '7,0.5' }] : []),
      ],
    },
    playlist: {
      music: null,
      musicMuted: false,
      formats: theme.data?.playlist?.formats || '4,7',
      transition: theme.data?.playlist?.transition || 'WipeRight,0.5',
      transitionFirstLast: theme.data?.playlist?.transitionFirstLast || 'Blur,0.5',
    },
  }

  return nextTheme
}
